import {Loader, Select} from '@mantine/core';
import Image from 'next/image';
import React, {useState, useEffect} from 'react';
import {MdFilterAlt, MdKeyboardArrowDown, MdWatchLater} from 'react-icons/md';
import {useInView} from 'react-intersection-observer';
import {
  ADVISER_CATEGORIES,
  type AdviserCategory,
} from '../../../core/entities/box/constants';
import {useInfiniteAdvisers} from '../../../core/repositories/boxRepository';
import type {ListedAdviserType} from '../../../core/repositories/boxRepositoryTypes';
import {formatDateDistanceToNow} from '../../../shared/services/formatDate';
import {CareerLabel} from './CareerLabel';

type selectAdviserProps = {
  selectAdviser: (adviser: ListedAdviserType) => void;
};

export const AdviserList = ({selectAdviser}: selectAdviserProps) => {
  const {ref, inView} = useInView();
  const [hashtagIDs, setHashtagIDs] = useState<number[]>([]);
  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteAdvisers({limit: 10, hashtagIDs});

  const refetchAdvisers = (hashtagIDs: number[]) => {
    setHashtagIDs(hashtagIDs);
    void refetch();
  };

  useEffect(() => {
    if (inView && hasNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  return (
    <div className="h-[626px]">
      <div className="flex items-center justify-between bg-background-1 p-16">
        <p className="text-lg font-bold leading text-black-1">回答者を選ぶ</p>
        <CategorySelectBox refetchAdvisers={refetchAdvisers} />
      </div>
      <div className="h-[556px] overflow-x-hidden overflow-y-scroll p-16 pt-0">
        {data?.pages?.flatMap((page, i) => {
          return (
            page.ok && (
              <React.Fragment key={`page-${i}`}>
                {page.value.advisers.map((adviser) => {
                  const formattedLastAnsweredAt = formatDateDistanceToNow(
                    adviser.lastAnsweredAt,
                  );
                  return (
                    <button
                      key={`adviser-${adviser.id}`}
                      ref={ref}
                      className="flex w-full justify-between border-b-[1px] border-solid border-b-line-1 pb-4 pt-16 pc:hover:opacity-80 pc:hover:duration-300 pc:hover:ease-in-out "
                      onClick={() => {
                        selectAdviser(adviser);
                      }}
                    >
                      <div className="w-[40px]">
                        <div className="mb-8 h-[40px] w-[40px] overflow-hidden rounded-full">
                          <Image
                            src={
                              adviser.iconImageURL
                                ? adviser.iconImageURL
                                : '/next-image/box/default.svg'
                            }
                            width={40}
                            height={40}
                            alt={
                              adviser.displayName
                                ? adviser.displayName
                                : '相談室回答者'
                            }
                          />
                        </div>
                        <CareerLabel isStudent={adviser.isStudent} />
                      </div>
                      <div className="w-[250px]">
                        <p className="text-left text leading text-black-1">
                          {adviser.displayName
                            ? adviser.displayName
                            : '相談室回答者'}
                        </p>
                        <div className="mt-8 max-h-[70px] bg-background-1 p-12">
                          <p className=" line-clamp-3 text-xs text-black-2">
                            {adviser.introduction}
                          </p>
                        </div>
                        {formattedLastAnsweredAt && (
                          <p className="flex items-center justify-end pt-4 text-icon-pc leading-sm text-black-4">
                            <i>
                              <MdWatchLater />
                            </i>
                            <span>{formattedLastAnsweredAt}前</span>
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </React.Fragment>
            )
          );
        })}
        {!isFetching && (!data || data.pages.length <= 0) && (
          <div className=" px-16 py-32">
            <p className=" text-center text-md leading text-black-1">
              回答者がいません
            </p>
          </div>
        )}
        {(isFetching || isFetchingNextPage) && (
          <div className="flex justify-center py-24">
            <Loader color="gray" size="lg" />
          </div>
        )}
      </div>
    </div>
  );
};

type categorySelectBoxProps = {
  refetchAdvisers: (hashtagIds: number[]) => void;
};

type SelectBoxOption = {
  value: string;
  hashtagIds: number[];
  label: string;
};
function convertToSelectBoxOptions(
  categories: AdviserCategory[],
): SelectBoxOption[] {
  return categories.map((t, i) => {
    return {
      value: i.toString(),
      hashtagIds: t.hashtagIds,
      label: t.name,
    };
  });
}

const CategorySelectBox = ({refetchAdvisers}: categorySelectBoxProps) => {
  const options = convertToSelectBoxOptions(ADVISER_CATEGORIES);
  // TODO: any以外を指定

  const handleChange = (newValue: string) => {
    const selected = options.find((o) => o.value === newValue);

    if (selected) {
      refetchAdvisers(selected.hashtagIds);
    }
  };

  return (
    <Select
      defaultValue={options.at(0)?.value}
      onChange={handleChange}
      data={options}
      icon={<MdFilterAlt size="11px" />}
      rightSection={<MdKeyboardArrowDown size="11px" color="#B5B5B5" />}
      styles={{
        input: {
          color: '#777777',
          fontSize: '14px',
          height: '32px',
          width: '152px',
          borderRadius: '2px',
          paddingLeft: '27px !important',
          borderColor: '#CFD0D7',
          border: '1px solid',
          textOverflow: 'ellipsis',
        },
        icon: {
          width: '27px',
        },
      }}
    />
  );
};
